import { useEffect } from 'react';
const KEYS = {
    legal: 'auth.return.legal',
    signIn: 'auth.return.signIn',
};
const FALLBACK = '/login';
// A same-origin path on one of the public auth surfaces. The leading single
// slash plus a fixed first segment also rules out protocol-relative '//host'.
const ALLOWED = /^\/(?:login|link|forgot-password|reset-password)(?:[/?#]|$)/;
// Tells this page load apart from earlier ones in the same tab: a record from
// another load was not written by a link the user followed to this page.
const PAGE_LOAD = `${Date.now()}-${Math.random()}`;
export function rememberReturnPath(slot, path) {
    try {
        const pending = { path, pageLoad: PAGE_LOAD };
        sessionStorage.setItem(KEYS[slot], JSON.stringify(pending));
    }
    catch {
        // Storage unavailable (privacy mode / quota): the side page falls back to /login.
    }
}
/** Where the side page should go back to. Reads only; useReturnPath claims it. */
export function recallReturnPath(slot) {
    const path = pendingPath(slot) ?? entryState()[KEYS[slot]];
    return typeof path === 'string' && ALLOWED.test(path) ? path : FALLBACK;
}
/**
 * The side page's back target. After the first render it claims the record the
 * linking page left: moved onto this history entry (a history write, so it
 * waits for the effect) and cleared from the tab, used or not.
 */
export function useReturnPath(slot) {
    const path = recallReturnPath(slot);
    useEffect(() => {
        const pending = pendingPath(slot);
        try {
            sessionStorage.removeItem(KEYS[slot]);
        }
        catch {
            // Storage unavailable: nothing was recorded either.
        }
        if (pending === undefined)
            return;
        try {
            window.history.replaceState({ ...entryState(), [KEYS[slot]]: pending }, '');
        }
        catch {
            // History unavailable: the path still served this visit, just not a reload.
        }
    }, [slot]);
    return path;
}
/** The current location, as a path the side page can link back to. */
export function currentPath() {
    return `${window.location.pathname}${window.location.search}`;
}
/** The path the linking page recorded during this page load, if any. */
function pendingPath(slot) {
    let raw;
    try {
        raw = sessionStorage.getItem(KEYS[slot]);
    }
    catch {
        return undefined;
    }
    if (!raw)
        return undefined;
    try {
        const value = JSON.parse(raw);
        const pending = value;
        if (pending && typeof pending.path === 'string' && pending.pageLoad === PAGE_LOAD) {
            return pending.path;
        }
    }
    catch {
        // Not a record this module wrote.
    }
    return undefined;
}
function entryState() {
    const state = window.history.state;
    return state && typeof state === 'object' ? state : {};
}
